/**
 * FAQ catalog for Cognidoor landing page.
 * Content validated against repo: plan names and inclusions match LandingCanonical.tsx;
 * billing answers aligned with checkout (PayFast, cancel anytime). Do not invent refund/trial policy.
 */

export type FaqSection = {
  id: string;
  title: string;
  description: string;
};

export type FaqItem = {
  id: string;
  section: string;
  question: string;
  short_answer: string;
  answer: string;
  featured: boolean;
  search_keywords: string[];
  /** If true, include in FAQPage JSON-LD (stable, non-volatile questions only). */
  forStructuredData?: boolean;
};

export const FAQ_SECTIONS: FaqSection[] = [
  { id: "platform", title: "About Cognidoor", description: "What Cognidoor is, who it is for, and why it is different." },
  { id: "products", title: "Products", description: "How each product works and what each one is designed to help with." },
  { id: "learning", title: "Coaching, feedback, and progress", description: "How feedback works, what is timed, and how users improve over time." },
  { id: "plans", title: "Plans and pricing", description: "How to choose the right plan and how billing works." },
  { id: "billing", title: "Payments and subscriptions", description: "Renewals, cancellations, refunds, and payment mechanics." },
  { id: "privacy", title: "Privacy and trust", description: "What happens to user responses, scoring, and protected data." },
  { id: "support", title: "Practical questions", description: "Getting started, best fit, and common usage questions." },
];

/** Plan names and scope match the live pricing section (LandingCanonical). */
const PLAN_NAMES = {
  seaWolf: "Sea-Wolf Assessment",
  casey: "Casey Assessment",
  bundle: "Assessments Bundle",
  mckFull: "McKinsey Track (Full)",
  bcgFull: "BCG Track (Full)",
  allAccess: "All-Access",
};

export const FAQ_ITEMS: FaqItem[] = [
  {
    id: "what-is-cognidoor",
    section: "platform",
    question: "What is Cognidoor?",
    short_answer: "Cognidoor helps you prepare for McKinsey and BCG assessments and case interviews.",
    answer: "Cognidoor brings your consulting prep into one place. You can practice McKinsey-style Sea Wolf, BCG Casey, and live case interviews, then see how you did and what to fix next. The goal is simple: help you practice in a way that feels closer to the real test, not just read frameworks.",
    featured: true,
    search_keywords: ["what is cognidoor", "cognidoor overview", "consulting prep platform"],
    forStructuredData: true,
  },
  {
    id: "who-is-cognidoor-for",
    section: "platform",
    question: "Who is Cognidoor for?",
    short_answer: "It is for people preparing for McKinsey or BCG assessments and case interviews, whether they are just starting or already interview-ready.",
    answer: "Cognidoor is built for candidates at different stages. If you are new, you can use the guided products and feedback to learn the basics. If you are further along, you can use timed simulations, firm-specific practice, and progress tracking to fix weak spots before real interviews.",
    featured: false,
    search_keywords: ["who is cognidoor for", "beginner consulting prep", "advanced consulting prep"],
    forStructuredData: true,
  },
  {
    id: "what-makes-cognidoor-different",
    section: "platform",
    question: "What makes Cognidoor different from other case platforms?",
    short_answer: "Cognidoor is built around the real tests and interviews candidates face, not just general case practice.",
    answer: "Most case platforms give you general practice. Cognidoor separates prep by the actual thing you are preparing for: McKinsey-style assessments, BCG Casey-style assessments, and live case interviews. When there is a clear right or wrong answer, the platform scores it in a structured way. When the value is in the conversation, the platform coaches you through it.",
    featured: false,
    search_keywords: ["why cognidoor", "what makes cognidoor different", "difference from other platforms"],
    forStructuredData: true,
  },
  {
    id: "do-i-need-to-be-advanced",
    section: "platform",
    question: "Do I need prior case interview experience to use Cognidoor?",
    short_answer: "No. Cognidoor is useful for both beginners and advanced candidates.",
    answer: "You do not need to arrive fully polished. Some parts of Cognidoor are designed to teach you how to think, while others are designed to test you under pressure. If you are early in your prep, the guided and feedback-heavy modes help you build confidence. If you are further along, the timed and firm-specific experiences help you refine performance.",
    featured: false,
    search_keywords: ["beginner", "new to case interviews", "do i need experience"],
    forStructuredData: true,
  },
  {
    id: "does-cognidoor-only-cover-mckinsey-and-bcg",
    section: "platform",
    question: "Does Cognidoor only help with McKinsey and BCG?",
    short_answer: "Cognidoor is primarily built around McKinsey and BCG-style prep.",
    answer: "The platform is especially strong where the hiring formats are specific and high stakes: McKinsey-style assessment practice, BCG Casey-style practice, and firm-style case interview coaching. It is still primarily centered on McKinsey and BCG, but the BCG-style case coaching can also help with Bain interviews because Bain cases are candidate-led too and reward many of the same skills: asking the right questions, driving the case forward, and turning messy information into a clear recommendation.",
    featured: false,
    search_keywords: ["mckinsey only", "bcg only", "other firms"],
    forStructuredData: true,
  },
  {
    id: "what-is-the-difference-between-sea-wolf-solver-and-simulator",
    section: "products",
    question: "What is the difference between the Sea Wolf Solver and the Sea Wolf Simulator?",
    short_answer: "The Solver solves the Sea Wolf task with you. The Simulator lets you practice the full Sea Wolf experience.",
    answer: "Use the Sea Wolf Solver when you want help choosing the right microbes and understanding why they work. Use the Sea Wolf Simulator when you want a more realistic run: multiple sites, step-by-step tasks, Coach mode or Exam Replica mode, tips while you work, and feedback at the end. Put simply: the Solver helps you solve it, while the Simulator helps you practice the real thing.",
    featured: true,
    search_keywords: ["sea wolf solver vs simulator", "difference sea wolf products"],
    forStructuredData: true,
  },
  {
    id: "what-is-red-rock-simulation",
    section: "products",
    question: "What is the Red Rock simulation?",
    short_answer: "Red Rock is a timed case practice tool. You work through a full case: study the brief, collect evidence, answer analysis questions, fill in a report, do graph and chart tasks, then finish with mini-cases and a clear score and feedback.",
    answer: "Red Rock is a timed case simulator. You get a case brief and then move through clear steps: Study (read the brief and understand the task), Investigation (gather evidence into a Research Journal), Analysis (answer questions and do the maths), Summary and Report (fill in the report using your evidence), then Graph Selection and Visualization (chart tasks), and finally mini-cases that build on the same logic. The run is timed so it feels like a real assessment. At the end you get a score, section-by-section feedback, and a recommendation for what to practice next. There are 17 cases to choose from. You can run in Exam mode (no hints during the run, like the real test) or Coach mode (same scoring, but you get tips and short recaps as you go so you can learn while you practice).",
    featured: true,
    search_keywords: ["what is red rock", "red rock simulation", "red rock cases", "mckinsey case practice"],
    forStructuredData: true,
  },
  {
    id: "red-rock-vs-sea-wolf-casey",
    section: "products",
    question: "How is Red Rock different from Sea Wolf and Casey?",
    short_answer: "Red Rock is full case practice with evidence and a report. Sea Wolf is ecosystem-style decisions. Casey is BCG timed questions and exhibits. Each matches a different type of test.",
    answer: "Red Rock is for full case-style practice: you read a brief, collect evidence, answer analysis questions, write a report, do graph and chart tasks, and complete mini-cases. It suits you if you want to practice the kind of multi-step case where evidence and reporting matter. Sea Wolf is different: it focuses on ecosystem-style decisions (for example, choosing and comparing options across sites) with a different flow and scoring. Casey is different again: it is built like the BCG Casey assessment, with timed questions, exhibits, and short answers, and a fixed question flow. So use Red Rock when you want case practice with evidence and a report; use Sea Wolf for ecosystem-style assessment practice; and use Casey for BCG Casey-style timed assessment practice.",
    featured: false,
    search_keywords: ["red rock vs sea wolf", "red rock vs casey", "difference red rock sea wolf casey"],
    forStructuredData: true,
  },
  {
    id: "red-rock-exam-vs-coach-mode",
    section: "products",
    question: "What is the difference between Exam and Coach mode in Red Rock?",
    short_answer: "Exam mode is strict: no hints during the run, like the real test. Coach mode uses the same scoring but gives you tips and short recaps as you go so you can learn while you practice.",
    answer: "In Exam mode, you run the case without any hints or feedback during the run. It is as close as possible to sitting the real assessment: you work through the steps, submit your answers, and only see how you did when the run is finished. In Coach mode, the case and scoring are the same, but you get live help: short tips, a quick read on how you are doing, and recap cards after certain sections (e.g. what went well, what to fix, and what to do next). So Exam mode is best when you want to test yourself under real conditions; Coach mode is best when you want to learn and improve while you work through the case.",
    featured: false,
    search_keywords: ["red rock exam mode", "red rock coach mode", "exam vs coach red rock"],
    forStructuredData: true,
  },
  {
    id: "what-is-the-bcg-casey-simulator",
    section: "products",
    question: "What is the BCG Casey Simulator?",
    short_answer: "It is a realistic, step-by-step practice version of the BCG Casey online assessment.",
    answer: "The BCG Casey Simulator is built to feel like the real Casey-style assessment. You answer timed questions, work through exhibits, do the math, and finish with a report. The steps and scoring stay consistent, which makes it useful when you want realistic practice and clear feedback after each run.",
    featured: true,
    search_keywords: ["what is bcg casey simulator", "casey simulator"],
    forStructuredData: true,
  },
  {
    id: "is-the-casey-simulator-ai",
    section: "products",
    question: "Is the Casey Simulator AI-driven?",
    short_answer: "No. The Casey Simulator is a structured simulator, not an open-ended AI chat.",
    answer: "The Casey Simulator does not use a free-chat AI interviewer. The questions, scoring, and report follow a fixed structure, so you get a stable practice experience and consistent feedback each time you use it.",
    featured: false,
    search_keywords: ["is casey simulator ai", "does casey simulator use llm"],
    forStructuredData: true,
  },
  {
    id: "what-is-the-mckinsey-ai-coach",
    section: "products",
    question: "What is the McKinsey AI Coach?",
    short_answer: "It is a McKinsey-style case practice coach that helps you prepare for both interviewer-led case interviews and newer AI-assisted case formats.",
    answer: "The McKinsey AI Coach gives you live McKinsey-style case practice that is closer to a real interview than a static prompt. McKinsey interviews are interviewer-led, so the coach helps you practise responding to questions, structuring your thinking clearly, and moving through the case under pressure. It also helps you prepare for McKinsey's newer Lilli-style AI assessment, where candidates work through a case with AI and need to prompt well, interpret the AI's responses, and use them intelligently. In other words, it helps you build both classic case interview skills and the newer skill of using AI well to solve a case.",
    featured: false,
    search_keywords: ["mckinsey ai coach", "war room mckinsey"],
    forStructuredData: true,
  },
  {
    id: "what-is-the-bcg-ai-coach",
    section: "products",
    question: "What is the BCG AI Coach?",
    short_answer: "It is a BCG-style case coach that helps you prepare for candidate-led interviews and the Casey chatbot assessment.",
    answer: "The BCG AI Coach gives you BCG-style live case practice built around the fact that BCG cases are candidate-led. That means you need to drive the conversation well: ask the right clarifying questions, decide what to explore next, and move toward the answer with confidence. The coach helps you practise that style so you get better at knowing what to ask, what to test, and how to turn messy information into a clear recommendation. It also helps you prepare for BCG's Casey chatbot assessment, where strong structure, business judgment, and steady progress through the case matter. The 'Are You Sure?' check can also stop you when it spots issues like unit mistakes or scale mistakes, so you can catch simple errors before moving on.",
    featured: false,
    search_keywords: ["bcg ai coach", "war room bcg", "casey mode"],
    forStructuredData: true,
  },
  {
    id: "what-does-ays-mean",
    section: "products",
    question: "What does 'Are You Sure?' mean in the BCG experience?",
    short_answer: "It is a checkpoint that asks you to revisit an answer when the system detects a likely issue.",
    answer: "AYS stands for 'Are You Sure?'. It is used in the BCG-style experience when the platform detects signs that you may want to double-check something, such as units, scale, or constraints. It is not there to punish you. It is there to help you catch avoidable errors before you lock in a response.",
    featured: false,
    search_keywords: ["ays", "are you sure"],
    forStructuredData: true,
  },
  {
    id: "does-cognidoor-have-dashboards",
    section: "products",
    question: "Does Cognidoor track my progress over time?",
    short_answer: "Yes. The dashboard gives you consistent feedback on performance and helps you improve.",
    answer: "Yes. Cognidoor includes a dashboard that helps you see how you are improving over time. It shows your recent work, your scores, and the areas where you are getting stronger or still losing points. The goal is to help you stop guessing and focus on the next thing that will actually improve your performance.",
    featured: true,
    search_keywords: ["dashboard", "progress dashboard", "skill tracking", "track progress"],
    forStructuredData: true,
  },
  {
    id: "what-feedback-do-i-get",
    section: "learning",
    question: "What kind of feedback do I get?",
    short_answer: "You get structured feedback, not just a vague score.",
    answer: "You do not just get a score. Depending on the product, you may see what you did well, what you missed, where you lost points, what a stronger answer looked like, and what to do next. In the coach products, you also get feedback on how the conversation went. In Sea Wolf, Red Rock, and Casey, the feedback is tied to how you solved the task.",
    featured: true,
    search_keywords: ["feedback", "what feedback do i get", "coach feedback"],
    forStructuredData: true,
  },
  {
    id: "are-scores-just-ai-opinions",
    section: "learning",
    question: "Are the scores just AI opinions?",
    short_answer: "Not in the way most users worry about.",
    answer: "No. Cognidoor does not treat scoring as a random opinion. In the coach products, the conversation is live, but the scoring rules and feedback logic are structured. In Casey and Sea Wolf, the logic is even more fixed. That makes the feedback easier to trust and easier to learn from.",
    featured: false,
    search_keywords: ["scoring deterministic", "ai scoring", "are scores subjective"],
    forStructuredData: true,
  },
  {
    id: "does-the-ai-coach-give-the-right-answer-away",
    section: "learning",
    question: "Does the coach just give away the answer?",
    short_answer: "No. The platform is designed to coach without breaking the exercise.",
    answer: "No. The platform is built to help you think better without ruining the exercise. In the AI coach products, the interviewer does not get the answer key. In Sea Wolf, Coach mode helps you learn, while Exam Replica mode is stricter and closer to the real test. The goal is to help you improve, not hand you the solution.",
    featured: false,
    search_keywords: ["does coach give away answers", "answer leakage"],
    forStructuredData: true,
  },
  {
    id: "is-practice-timed",
    section: "learning",
    question: "Are the practice experiences timed?",
    short_answer: "Yes, timing is part of the design in the coach and simulator experiences.",
    answer: "Yes, many of them are timed. That matters because the real assessments and interviews are timed too. Cognidoor uses timing where it matters most, especially in Casey and the live coach flows, so you can practice not only getting the answer right but doing it under pressure.",
    featured: false,
    search_keywords: ["timed", "is practice timed", "assessment conditions"],
    forStructuredData: true,
  },
  {
    id: "do-i-need-a-case-partner",
    section: "learning",
    question: "Do I need a case partner to use Cognidoor?",
    short_answer: "No. Cognidoor is designed so you can practice on your own.",
    answer: "No. You can practice on your own. That is useful when you want to practice often, get feedback straight away, or work at times when finding a partner is hard.",
    featured: false,
    search_keywords: ["case partner", "do i need a partner"],
    forStructuredData: true,
  },
  {
    id: "can-beginners-use-the-ai-coaches",
    section: "learning",
    question: "Can beginners use the AI coaches, or should they start somewhere else?",
    short_answer: "Beginners can use them, but many will improve faster if they combine them with more guided practice.",
    answer: "Beginners can use the AI coaches, but many people improve faster if they also use the more guided products. If you are very new, it often helps to build the basics first, then use the live coach to practice under pressure.",
    featured: false,
    search_keywords: ["beginners ai coach", "start with ai coach"],
    forStructuredData: true,
  },
  {
    id: "how-does-progress-tracking-work",
    section: "learning",
    question: "How does progress tracking and the dashboard work?",
    short_answer: "The dashboard shows your progress over time so you can improve consistently.",
    answer: "The dashboard helps you look beyond one session. You can see your recent work, how your scores are moving, and which areas still need attention. That makes it easier to decide what to practice next instead of repeating the same mistakes.",
    featured: true,
    search_keywords: ["how progress tracking works", "evidence", "dashboard", "track in progress"],
    forStructuredData: true,
  },
  {
    id: "how-do-pricing-and-plans-work",
    section: "plans",
    question: "How do Cognidoor's plans work?",
    short_answer: "Cognidoor uses product-based plans so you can buy only the prep you need or choose a fuller bundle.",
    answer: "Cognidoor's pricing is organized around what you are actually trying to prepare for. There are plans for assessment-style practice (Sea-Wolf Assessment, Casey Assessment, or both in the Assessments Bundle), firm-specific full-track plans (McKinsey Track (Full) and BCG Track (Full)), and an All-Access option. The pricing section on this page shows the current plans and what each includes.",
    featured: true,
    search_keywords: ["pricing", "plans", "how pricing works"],
    forStructuredData: false,
  },
  {
    id: "which-plan-should-i-pick",
    section: "plans",
    question: "Which plan should I pick?",
    short_answer: "Pick based on the stage and firm you are preparing for.",
    answer: `If you mainly want McKinsey-style Sea Wolf practice, start with ${PLAN_NAMES.seaWolf}. If you mainly want BCG Casey-style assessment practice, start with ${PLAN_NAMES.casey}. If you want both assessments without the AI coaches, the ${PLAN_NAMES.bundle} is a good fit. If you want broader McKinsey case prep including the AI coach, ${PLAN_NAMES.mckFull} is the better fit. For broader BCG prep including the AI coach, ${PLAN_NAMES.bcgFull} makes more sense. If you want the whole platform, ${PLAN_NAMES.allAccess} is the simplest option.`,
    featured: true,
    search_keywords: ["which plan should i pick", "best plan", "which cognidoor plan"],
    forStructuredData: false,
  },
  {
    id: "what-plans-are-available",
    section: "plans",
    question: "What plans are available right now?",
    short_answer: "The current plan set includes assessment-only plans, full-track plans per firm, and an All-Access bundle.",
    answer: `The plans you see on the pricing section are: ${PLAN_NAMES.seaWolf}, ${PLAN_NAMES.casey}, ${PLAN_NAMES.bundle}, ${PLAN_NAMES.mckFull}, ${PLAN_NAMES.bcgFull}, and ${PLAN_NAMES.allAccess}. Each plan can be billed monthly or as a six-month term. Check the pricing section on this page for the exact names, prices, and included features.`,
    featured: false,
    search_keywords: ["available plans", "plan list"],
    forStructuredData: false,
  },
  {
    id: "what-is-in-sea-wolf-core",
    section: "plans",
    question: "What is included in Sea-Wolf Assessment?",
    short_answer: "Sea-Wolf Assessment is the focused option for McKinsey-style Sea Wolf practice.",
    answer: `${PLAN_NAMES.seaWolf} includes the Sea Wolf solver, Sea Wolf simulation, and 15+ Red Rock cases. It is best for candidates who want focused McKinsey-style assessment practice without the AI case coach.`,
    featured: false,
    search_keywords: ["sea wolf assessment", "what is included sea wolf"],
    forStructuredData: false,
  },
  {
    id: "what-is-in-casey-core",
    section: "plans",
    question: "What is included in Casey Assessment?",
    short_answer: "Casey Assessment is the focused option for BCG Casey-style assessment practice.",
    answer: `${PLAN_NAMES.casey} includes the BCG Casey simulation. It is the simplest choice if your main need is BCG-style timed assessment practice rather than the AI case coach.`,
    featured: false,
    search_keywords: ["casey assessment", "what is included casey"],
    forStructuredData: false,
  },
  {
    id: "what-is-in-mckinsey-full",
    section: "plans",
    question: "What is included in McKinsey Track (Full)?",
    short_answer: "McKinsey Track (Full) is the broader McKinsey-focused prep plan.",
    answer: `${PLAN_NAMES.mckFull} includes the Sea Wolf solver, Sea Wolf simulation, McKinsey Lilli AI case Coach, and 15+ Red Rock cases. It is designed for candidates who want both assessment-style practice and live case-interview coaching on the McKinsey side.`,
    featured: false,
    search_keywords: ["mckinsey track full", "what is included mckinsey"],
    forStructuredData: false,
  },
  {
    id: "what-is-in-bcg-full",
    section: "plans",
    question: "What is included in BCG Track (Full)?",
    short_answer: "BCG Track (Full) is the broader BCG-focused prep plan.",
    answer: `${PLAN_NAMES.bcgFull} includes the BCG Casey simulation and the BCG Candidate-led AI case Coach. It is best for candidates who want both Casey-style assessment work and live BCG-style case practice.`,
    featured: false,
    search_keywords: ["bcg track full", "what is included bcg"],
    forStructuredData: false,
  },
  {
    id: "what-is-in-all-access",
    section: "plans",
    question: "What is included in the All-Access plan?",
    short_answer: "All-Access is the widest plan and includes the full platform.",
    answer: `${PLAN_NAMES.allAccess} includes McKinsey Track (Full), BCG Track (Full), Sea-Wolf Assessment, Casey Assessment, and 15+ Red Rock cases. It is the cleanest choice if you want the whole platform without having to think about product boundaries.`,
    featured: false,
    search_keywords: ["all access", "full bundle", "everything included"],
    forStructuredData: false,
  },
  {
    id: "monthly-vs-six-month",
    section: "plans",
    question: "What is the difference between the monthly and six-month options?",
    short_answer: "The monthly option gives you more flexibility; the six-month option is meant for longer prep windows and lower effective cost.",
    answer: "Most candidates choose based on how close they are to interviews and how much flexibility they want. Monthly plans are easier if your timeline is uncertain. Six-month plans usually make more sense if you know you want a longer runway and want a lower effective cost over time. The pricing section on this page shows both options for each plan.",
    featured: false,
    search_keywords: ["monthly vs six month", "billing term", "payment schedule"],
    forStructuredData: false,
  },
  {
    id: "do-bundles-save-money",
    section: "plans",
    question: "Do the bundles save money compared with buying separate plans?",
    short_answer: "The bundle plans are designed to be simpler and more attractive when you want more than one product area.",
    answer: "The Assessments Bundle and All-Access plans are designed to be simpler and more attractive for candidates who want more than one product area. Compare the six-month and monthly prices for the bundle against the sum of the individual plans on the pricing section to see the current savings.",
    featured: false,
    search_keywords: ["bundle savings", "is bundle cheaper"],
    forStructuredData: false,
  },
  {
    id: "can-i-upgrade-later",
    section: "billing",
    question: "Can I upgrade later if I start with a smaller plan?",
    short_answer: "Many candidates start focused and widen their prep later; upgrade options depend on the current billing setup.",
    answer: "From a user-experience standpoint, upgrading is intended for candidates who start focused and then want broader prep. How upgrades and plan changes are handled (including proration) depends on the current checkout and subscription setup. Check the pricing section and your account area for the latest options.",
    featured: true,
    search_keywords: ["upgrade later", "change plan"],
    forStructuredData: false,
  },
  {
    id: "how-do-payments-work",
    section: "billing",
    question: "How do payments work?",
    short_answer: "Payments are processed through the site when you choose a plan and complete checkout.",
    answer: "When you choose a plan on the pricing section, you are taken to checkout. Payment is processed securely through our payment provider. You are billed for the term you selected (monthly or six-month). Subscription and billing can be managed from your account. Check the checkout flow and any terms shown there for renewal and cancellation details.",
    featured: true,
    search_keywords: ["payments", "how do payments work", "billing"],
    forStructuredData: false,
  },
  {
    id: "what-payment-methods-do-you-accept",
    section: "billing",
    question: "What payment methods do you accept?",
    short_answer: "Payment methods are shown at checkout; typically card and other methods supported by our payment provider.",
    answer: "Accepted payment methods are shown when you reach checkout. Cognidoor uses a secure payment provider to process subscriptions. Complete the checkout flow to see the exact options available in your region.",
    featured: false,
    search_keywords: ["payment methods", "card", "payfast"],
    forStructuredData: false,
  },
  {
    id: "do-plans-auto-renew",
    section: "billing",
    question: "Do plans auto-renew?",
    short_answer: "Subscription behavior is explained at checkout and in the terms you accept when you subscribe.",
    answer: "Whether your plan renews automatically depends on the subscription type you choose at checkout. The checkout page and the terms you accept when you subscribe explain renewal behavior. If you are unsure, check your account or the confirmation email after purchase.",
    featured: false,
    search_keywords: ["auto renew", "renewal", "subscription renew"],
    forStructuredData: false,
  },
  {
    id: "can-i-cancel-anytime",
    section: "billing",
    question: "Can I cancel anytime?",
    short_answer: "Yes. You can cancel your subscription; access typically continues until the end of the paid term.",
    answer: "You can cancel your subscription. The checkout flow states that you can always cancel your subscription. When cancellation takes effect and whether access continues through the end of the paid period will be set out in the terms and in your account. If you need help, contact support.",
    featured: false,
    search_keywords: ["cancel anytime", "cancel subscription"],
    forStructuredData: false,
  },
  {
    id: "do-you-offer-refunds",
    section: "billing",
    question: "Do you offer refunds?",
    short_answer: "Yes. If paid product features fail, you can contact support anytime to request a refund review.",
    answer: "Yes. If a paid Cognidoor feature you bought is not working properly or fails to deliver the core functionality described on the site, you can contact support at any time to request a refund review. We want the product to be trustworthy, not frustrating. Refund handling still follows the terms shown at checkout and Cognidoor's legal terms, but support is the right place to raise the issue and get help.",
    featured: false,
    search_keywords: ["refunds", "refund policy", "money back"],
    forStructuredData: false,
  },
  {
    id: "do-you-have-a-free-trial-or-free-version",
    section: "billing",
    question: "Is there a free trial or free version?",
    short_answer: "You can try demos from the main page before subscribing.",
    answer: "Cognidoor offers demos so you can try the product before you subscribe. The main page and pricing section show how to start. If you want the simplest paid option, the assessment-only plans are the best place to start.",
    featured: false,
    search_keywords: ["free trial", "free version", "try before buy"],
    forStructuredData: false,
  },
  {
    id: "is-my-data-private",
    section: "privacy",
    question: "Is my interview and practice data private?",
    short_answer: "Yes. Your data is kept private and used only for your own profile.",
    answer: "Your data is kept private. Cognidoor uses it only for your own candidate profile and to power your coaching and progress views. It is not shared, sold, or leaked to any third parties. We do not use your responses for model training, and our AI models are not trained on your data.",
    featured: true,
    search_keywords: ["privacy", "is my data private", "data security"],
    forStructuredData: true,
  },
  {
    id: "does-the-ai-interviewer-see-answer-keys",
    section: "privacy",
    question: "Does the AI interviewer get access to the answer key?",
    short_answer: "No. The interviewer is designed not to receive direct ground-truth answers.",
    answer: "This is one of Cognidoor's strongest trust features. The system is set up so the interviewer does not receive direct answer-key data. Retrieval and reveal rules are also used to control what gets exposed and when. That helps the coach stay realistic without simply leaking the solution.",
    featured: false,
    search_keywords: ["answer keys", "ground truth", "does ai see answers"],
    forStructuredData: true,
  },
  {
    id: "does-cognidoor-use-my-data-to-train-models",
    section: "privacy",
    question: "Does Cognidoor use my responses to train AI models?",
    short_answer: "No. Your data is not used to train models.",
    answer: "No. Cognidoor does not use your responses or practice data to train AI models. Your data is used only for your own profile and feedback. Models are not trained using your data.",
    featured: false,
    search_keywords: ["train models", "use my data", "llm training"],
    forStructuredData: true,
  },
  {
    id: "what-data-is-stored",
    section: "privacy",
    question: "What kind of data does Cognidoor store?",
    short_answer: "Only what’s needed for your profile and feedback; nothing is shared or sold.",
    answer: "Cognidoor stores only what is needed to run your sessions and show your progress on your own dashboard—for example, scores and progress so you can see how you’re improving. This data is kept private, used only for your candidate profile, and is not shared, sold, or used to train models.",
    featured: false,
    search_keywords: ["what data is stored", "session data", "telemetry"],
    forStructuredData: true,
  },
  {
    id: "is-my-data-shared-or-sold",
    section: "privacy",
    question: "Is my data shared or sold?",
    short_answer: "No. Your data is never shared, sold, or leaked.",
    answer: "No. Your practice and interview data is kept private and is only used for your own profile and to give you consistent feedback. It is not shared with third parties, not sold, and not leaked for any purpose.",
    featured: false,
    search_keywords: ["data shared", "data sold", "privacy"],
    forStructuredData: true,
  },
  {
    id: "is-scoring-fair",
    section: "privacy",
    question: "How does Cognidoor keep scoring trustworthy?",
    short_answer: "By separating open-ended interaction from structured scoring.",
    answer: "Cognidoor does not make everything depend on one black-box judgment. In the AI coach products, the conversation is live, but the scoring and feedback rules are structured. In the simulator products, the logic is even more fixed. That makes the feedback easier to trust and easier to learn from.",
    featured: false,
    search_keywords: ["fair scoring", "reliable scoring", "trust scores"],
    forStructuredData: true,
  },
  {
    id: "where-should-i-start",
    section: "support",
    question: "Where should I start if I'm overwhelmed?",
    short_answer: "Start with the product that matches the next test or interview you are facing.",
    answer: "Start with the thing you are most likely to face next. If that is a McKinsey-style ecosystem assessment, start with Sea Wolf. If it is a full case-style assessment with evidence and a report, start with Red Rock. If it is BCG Casey, start with the Casey simulator. If your next step is the live interview, use the AI coach for your target firm. The easiest way to get overwhelmed is to try everything at once.",
    featured: false,
    search_keywords: ["where should i start", "best starting point"],
    forStructuredData: true,
  },
  {
    id: "how-often-should-i-practice",
    section: "support",
    question: "How often should I practice on Cognidoor?",
    short_answer: "Consistency matters more than random intensity.",
    answer: "Most people improve faster with regular practice than with one huge cram session. The right schedule depends on your timeline, but the key is to practice, review what went wrong, and come back again. Cognidoor is built for that kind of repeat practice.",
    featured: false,
    search_keywords: ["how often should i practice", "prep cadence"],
    forStructuredData: true,
  },
  {
    id: "can-i-use-cognidoor-for-both-firms",
    section: "support",
    question: "Can I use Cognidoor if I'm applying to both McKinsey and BCG?",
    short_answer: "Yes. That is one of the strongest reasons to use Cognidoor.",
    answer: "Yes. Many candidates are applying to both firms at the same time. Cognidoor works well for that because it keeps the different practice types separate instead of forcing everything into one format. You can prepare for McKinsey-style and BCG-style tests in the same platform.",
    featured: false,
    search_keywords: ["both firms", "mckinsey and bcg", "cross prep"],
    forStructuredData: true,
  },
  {
    id: "what-happens-when-i-get-something-wrong",
    section: "support",
    question: "What happens when I get something wrong?",
    short_answer: "You should get feedback that helps you understand and improve, not just a dead end.",
    answer: "You should get help, not just a red mark. Depending on the product, Cognidoor can show you what was wrong, why it was wrong, and what to do differently next time. The point is to help you improve from mistakes, not just point them out.",
    featured: false,
    search_keywords: ["wrong answer", "mistakes", "what happens if i fail"],
    forStructuredData: true,
  },
  {
    id: "can-cognidoor-replace-human-coaching",
    section: "support",
    question: "Can Cognidoor replace human coaching entirely?",
    short_answer: "For many users it can replace a large amount of routine practice, but some people will still value human coaching for nuanced personal feedback.",
    answer: "For many people, Cognidoor can replace a lot of routine practice. You can get frequent practice, immediate feedback, and firm-specific prep without having to book a human coach every time. Some people will still want human coaching for very personal or nuanced feedback, but Cognidoor can reduce how much you need it.",
    featured: false,
    search_keywords: ["replace human coaching", "human coach"],
    forStructuredData: true,
  },
  {
    id: "why-does-the-platform-separate-products",
    section: "support",
    question: "Why are there different products instead of one prep experience?",
    short_answer: "Because the hiring hurdles are different, and one approach often blurs them together.",
    answer: "Because these are different tests. McKinsey-style assessments, BCG Casey, and live case interviews do not work the same way, so they should not all be trained the same way. Cognidoor separates them so each product can feel closer to the real thing.",
    featured: false,
    search_keywords: ["why separate products", "why different products"],
    forStructuredData: true,
  },
  {
    id: "can-i-practice-at-my-own-pace",
    section: "support",
    question: "Can I practice at my own pace?",
    short_answer: "Yes, although some experiences intentionally introduce timing pressure.",
    answer: "Yes. You can choose when to practice and how often to use the platform. Some products are still timed, because real assessments and interviews are timed too. So you keep the flexibility of self-practice while still preparing for real pressure.",
    featured: false,
    search_keywords: ["own pace", "self paced"],
    forStructuredData: true,
  },
];

/** Featured item IDs for "Highlights" — 5–6 highest-weight questions by helpfulness (differentiation, plan choice, core products, value prop, trust). */
export const FEATURED_FAQ_IDS: string[] = [
  "what-makes-cognidoor-different",
  "which-plan-should-i-pick",
  "what-is-the-difference-between-sea-wolf-solver-and-simulator",
  "what-is-red-rock-simulation",
  "what-is-the-bcg-casey-simulator",
  "what-feedback-do-i-get",
  "is-my-data-private",
];

export function getItemsBySection(sectionId: string): FaqItem[] {
  return FAQ_ITEMS.filter((item) => item.section === sectionId);
}

export function getFeaturedItems(): FaqItem[] {
  return FEATURED_FAQ_IDS.map((id) => FAQ_ITEMS.find((item) => item.id === id)).filter(Boolean) as FaqItem[];
}

export function getItemById(id: string): FaqItem | undefined {
  return FAQ_ITEMS.find((item) => item.id === id);
}

export function searchItems(query: string): FaqItem[] {
  if (!query || query.trim().length === 0) return FAQ_ITEMS;
  const q = query.trim().toLowerCase();
  return FAQ_ITEMS.filter(
    (item) =>
      item.question.toLowerCase().includes(q) ||
      item.short_answer.toLowerCase().includes(q) ||
      item.answer.toLowerCase().includes(q) ||
      item.search_keywords.some((k) => k.toLowerCase().includes(q))
  );
}

/** Build FAQPage JSON-LD for stable (non-volatile) questions only. Use on landing for SEO. */
export function buildFaqPageStructuredData(): object {
  const items = FAQ_ITEMS.filter((item) => item.forStructuredData === true);
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}
